import axios from "axios.js"

const shouldTreatAsApiEndpoint = (path) => {
  if (!path) return false
  const normalized = path.replace(/^\/+/, "").toLowerCase()
  return normalized.startsWith("quitance/download") || normalized.startsWith("quitance/generate")
}

const normalizeApiUrl = (path) =>
  "/api/" +
  path
    .replace(/^\/+/, "")
    .replace(/^api\//i, "")

const normalizeApiEndpointPath = (path) => {
  if (!path) return path
  if (/^https?:\/\//i.test(path)) return path
  return path
    .replace(/^\/+/, "")
    .replace(/^api\//i, "")
}

const normalizeAxiosRequestUrl = (url) => {
  if (!url) return url
  if (/^https?:\/\//i.test(url)) return url
  return url
    .replace(/^\/+/, "")
    .replace(/^api\//i, "")
}

const normalizeRelativePath = (input) => {
  if (!input) return null
  let value = input
  if (/^https?:\/\//i.test(input)) {
    try {
      value = new URL(input).pathname || ""
    } catch (err) {
      return null
    }
  }
  return value
    .replace(/^api\/static\//i, "")
    .replace(/^api\/files\//i, "")
    .replace(/^files\//i, "")
    .replace(/^static\//i, "")
    .replace(/^\/+/, "")
}

const openInNewTab = (url, name) => {
  const link = document.createElement("a")
  const href = /^https?:\/\//i.test(url) ? url : `/${url.replace(/^\/+/, "")}`
  link.href = href
  link.target = "_blank"
  if (name) link.setAttribute("download", name)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const downloadBlob = (url, name) =>
  axios({
    url: normalizeAxiosRequestUrl(url),
    method: "GET",
    responseType: "blob",
  }).then((response) => {
    const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement("a")
    link.href = blobUrl
    link.setAttribute("download", name)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(blobUrl)
  })

const isCrossOrigin = (url) => {
  try {
    const absolute = new URL(url, window.location.origin)
    return absolute.origin !== window.location.origin
  } catch (err) {
    return false
  }
}

const tryDownloadWithFallback = (primaryUrl, fallbackUrl, name) =>
  downloadBlob(primaryUrl, name).catch((error) => {
    if (fallbackUrl) {
      if (isCrossOrigin(fallbackUrl)) {
        openInNewTab(fallbackUrl, name)
        return
      }
      return downloadBlob(fallbackUrl, name).catch(() => {
        openInNewTab(fallbackUrl, name)
      })
    }
    if (isCrossOrigin(primaryUrl)) {
      openInNewTab(primaryUrl, name)
      return
    }
    throw error
  })

export default (path, name) => {
  if (!path) return
  let directUrl = path
  if (axios.static) {
    if (/^https?:\/\//i.test(path)) {
      directUrl = path
    } else if (shouldTreatAsApiEndpoint(path)) {
      directUrl = normalizeApiEndpointPath(path)
    } else {
      directUrl = axios.static(path)
    }
  }

  if (shouldTreatAsApiEndpoint(path)) {
    return tryDownloadWithFallback(directUrl, null, name)
  }

  const relativePath = normalizeRelativePath(path)
  const proxyUrl = relativePath ? `files/${relativePath}` : null
  const primaryUrl = proxyUrl || directUrl
  const secondaryUrl =
    proxyUrl && directUrl && proxyUrl !== directUrl ? directUrl : null

  if (proxyUrl) {
    return tryDownloadWithFallback(primaryUrl, secondaryUrl, name)
  }

  if (isCrossOrigin(primaryUrl)) {
    openInNewTab(primaryUrl, name)
    return
  }

  return tryDownloadWithFallback(primaryUrl, null, name)
}
